import React, { useEffect, useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription } from '@/components/ui/alert'
import type { Gallery } from '@/features/gallery/types'
import useSanityGallery from '@/features/gallery/hooks/useSanityGallery'
import { ExternalLink, RefreshCcw, Upload, CheckCircle, Archive, Trash2, Link2, Edit, Save, X } from 'lucide-react'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'

interface SanityGalleryManagerProps {
  gallery: Gallery
  coverImageUrl?: string
  onSanityIdChange?: (id: string | null) => void
}

/**
 * SanityGalleryManager
 * Lightweight manager that connects a local Gallery to Sanity CMS using the
 * existing useSanityGallery hook. Intentionally minimal UI to avoid adding
 * complexity while unblocking import resolution and providing useful actions.
 */
export function SanityGalleryManager({ gallery, coverImageUrl, onSanityIdChange }: SanityGalleryManagerProps) {
  const {
    isLoading,
    sanityGallery,
    sanityId,
    createInSanity,
    updateInSanity,
    publishInSanity,
    archiveInSanity,
    deleteFromSanity,
    setSanityId,
  } = useSanityGallery(gallery)

  const [manualId, setManualId] = useState('')
  const [isEditing, setIsEditing] = useState(false)
  const [editForm, setEditForm] = useState({
    title: '',
    description: '',
    publishingStatus: 'draft' as 'draft' | 'review' | 'published' | 'featured' | 'archived',
    category: 'residential' as string,
    tags: '' as string
  })

  // Propagate sanityId changes to parent if requested
  useEffect(() => {
    if (onSanityIdChange) onSanityIdChange(sanityId)
  }, [sanityId, onSanityIdChange])

  // Initialize edit form when sanityGallery changes
  useEffect(() => {
    if (sanityGallery && !isEditing) {
      setEditForm({
        title: sanityGallery.title || gallery.title,
        description: sanityGallery.description || gallery.description || '',
        publishingStatus: sanityGallery.publishingStatus || 'draft',
        category: sanityGallery.category || 'residential',
        tags: sanityGallery.tags?.join(', ') || ''
      })
    }
  }, [sanityGallery, gallery, isEditing])

  const handleCreate = async () => {
    const id = await createInSanity(gallery, coverImageUrl)
    if (id) setSanityId(id)
  }

  const handleConnect = () => {
    if (!manualId.trim()) return
    setSanityId(manualId.trim())
    setManualId('')
  }

  const handleStartEdit = () => {
    setIsEditing(true)
  }

  const handleCancelEdit = () => {
    setIsEditing(false)
    // Reset form to current values
    if (sanityGallery) {
      setEditForm({
        title: sanityGallery.title || gallery.title,
        description: sanityGallery.description || gallery.description || '',
        publishingStatus: sanityGallery.publishingStatus || 'draft',
        category: sanityGallery.category || 'residential',
        tags: sanityGallery.tags?.join(', ') || ''
      })
    }
  }

  const handleSaveEdit = async () => {
    try {
      // Create an instruction for updating the gallery
      const instruction = `Update the gallery with these details:
        - Title: "${editForm.title}"
        - Description: "${editForm.description}"
        - Publishing Status: "${editForm.publishingStatus}"
        - Category: "${editForm.category}"
        ${editForm.tags ? `- Tags: "${editForm.tags}"` : ''}
      
      Please update this gallery document in Sanity CMS.`

      // Use the existing updateInSanity method but pass a custom gallery object
      const updatedGallery = {
        ...gallery,
        title: editForm.title,
        description: editForm.description
      }
      
      await updateInSanity(updatedGallery)
      setIsEditing(false)
    } catch (error) {
      console.error('Failed to save gallery edits:', error)
    }
  }

  const getStudioUrl = (documentId?: string | null) => {
    if (!documentId) return 'https://2jg18uur.sanity.studio'
    return `https://2jg18uur.sanity.studio/structure/gallery;${documentId}`
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-base">Sanity CMS</CardTitle>
        {sanityId ? (
          <Badge variant="secondary" className="text-xs">Connected</Badge>
        ) : (
          <Badge variant="outline" className="text-xs">Not Connected</Badge>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        {!sanityId ? (
          <div className="space-y-3">
            <Alert>
              <AlertDescription className="text-sm">
                Sync this gallery with Sanity to publish on the marketing site.
              </AlertDescription>
            </Alert>
            <div className="flex flex-wrap gap-2">
              <Button onClick={handleCreate} disabled={isLoading} className="flex items-center gap-2">
                <Upload className="h-4 w-4" />
                {isLoading ? 'Creating…' : 'Create in Sanity'}
              </Button>
            </div>
            <div className="flex items-center gap-2">
              <Input
                value={manualId}
                onChange={(e) => setManualId(e.target.value)}
                placeholder="Connect existing Sanity ID (e.g. doc _id)"
              />
              <Button variant="outline" onClick={handleConnect} disabled={!manualId.trim() || isLoading}>
                <Link2 className="h-4 w-4 mr-1" /> Connect
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {!isEditing ? (
              <>
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                  <div className="text-sm text-muted-foreground">
                    <div>
                      <span className="font-medium text-foreground">Sanity ID:</span> {sanityId}
                    </div>
                    {sanityGallery?.publishingStatus && (
                      <div>
                        <span className="font-medium text-foreground">Status:</span> {sanityGallery.publishingStatus}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button variant="outline" onClick={handleStartEdit} disabled={isLoading}>
                      <Edit className="h-4 w-4 mr-2" /> Edit
                    </Button>
                    <Button variant="outline" onClick={() => updateInSanity(gallery)} disabled={isLoading}>
                      <RefreshCcw className="h-4 w-4 mr-2" /> {isLoading ? 'Syncing…' : 'Sync'}
                    </Button>
                    <Button className="bg-chart-5 text-primary-foreground hover:bg-chart-5/90" onClick={() => publishInSanity()} disabled={isLoading}>
                      <CheckCircle className="h-4 w-4 mr-2" /> Publish
                    </Button>
                    <Button variant="outline" onClick={() => archiveInSanity()} disabled={isLoading}>
                      <Archive className="h-4 w-4 mr-2" /> Archive
                    </Button>
                    <Button variant="destructive" onClick={() => deleteFromSanity()} disabled={isLoading}>
                      <Trash2 className="h-4 w-4 mr-2" /> Delete
                    </Button>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Button variant="ghost" className="px-0 h-auto text-sm" asChild>
                    <a href={getStudioUrl(sanityId)} target="_blank" rel="noreferrer" className="inline-flex items-center">
                      Open Sanity Studio <ExternalLink className="h-3 w-3 ml-1" />
                    </a>
                  </Button>
                </div>
              </>
            ) : (
              <div className="space-y-4">
                <div className="grid gap-4">
                  <div>
                    <label className="text-sm font-medium">Title</label>
                    <Input
                      value={editForm.title}
                      onChange={(e) => setEditForm(prev => ({ ...prev, title: e.target.value }))}
                      placeholder="Gallery title"
                    />
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium">Description</label>
                    <Textarea
                      value={editForm.description}
                      onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Gallery description"
                      rows={3}
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium">Publishing Status</label>
                      <Select 
                        value={editForm.publishingStatus} 
                        onValueChange={(value) => setEditForm(prev => ({ ...prev, publishingStatus: value as any }))}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="draft">Draft</SelectItem>
                          <SelectItem value="review">Review</SelectItem>
                          <SelectItem value="published">Published</SelectItem>
                          <SelectItem value="featured">Featured</SelectItem>
                          <SelectItem value="archived">Archived</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <label className="text-sm font-medium">Category</label>
                      <Select 
                        value={editForm.category} 
                        onValueChange={(value) => setEditForm(prev => ({ ...prev, category: value }))}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select category" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="flagstone">Flagstone Work</SelectItem>
                          <SelectItem value="brick">Brick Examples</SelectItem>
                          <SelectItem value="stone">Stone Work</SelectItem>
                          <SelectItem value="materials">Mortar & Materials</SelectItem>
                          <SelectItem value="techniques">Techniques & Process</SelectItem>
                          <SelectItem value="before-after">Before & After</SelectItem>
                          <SelectItem value="heritage">Heritage & Restoration</SelectItem>
                          <SelectItem value="commercial">Commercial Work</SelectItem>
                          <SelectItem value="residential">Residential Work</SelectItem>
                          <SelectItem value="chimney">Chimney Work</SelectItem>
                          <SelectItem value="foundation">Foundation Work</SelectItem>
                          <SelectItem value="tools">Tools & Equipment</SelectItem>
                          <SelectItem value="seasonal">Seasonal Work</SelectItem>
                          <SelectItem value="problems">Problem Examples</SelectItem>
                          <SelectItem value="solutions">Solutions</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium">Tags</label>
                    <Input
                      value={editForm.tags}
                      onChange={(e) => setEditForm(prev => ({ ...prev, tags: e.target.value }))}
                      placeholder="Enter tags separated by commas"
                    />
                    <p className="text-xs text-muted-foreground mt-1">Separate multiple tags with commas</p>
                  </div>
                </div>
                
                <div className="flex gap-2 pt-4 border-t">
                  <Button onClick={handleSaveEdit} disabled={isLoading}>
                    <Save className="h-4 w-4 mr-2" />
                    {isLoading ? 'Saving…' : 'Save Changes'}
                  </Button>
                  <Button variant="outline" onClick={handleCancelEdit} disabled={isLoading}>
                    <X className="h-4 w-4 mr-2" />
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

export default SanityGalleryManager
